import { useCallback, useState } from 'react';

import { type Experiment, useExperiment } from './use-experiment';

/**
 * Hook that manages exposure to an experiment based on experiment manifest.
 * This is useful for firing exposure events for experiments that require
 * the initial evaluation of the experiment to happen outside of
 * the React lifecyle (e.g., using `getExperiment`).
 *
 * Returns a boolean indicating if the exposure event has been fired,
 * and a function to fire the exposure event.
 * If the event has already been fired, the function will log a warning instead.
 * @param experiment The experiment manifest
 * @param metadata The metadata for tracking events
 * @returns [boolean, () => void]
 */
export function useExperimentExposure<T extends Experiment<unknown>>(
	experiment: T,
	metadata: object = {},
): [boolean, () => void] {
	const { experimentName, options } = experiment;
	const experimentStatus = useExperiment(experiment, metadata);

	const [exposureFired, setExposureFired] = useState(() => {
		return experimentStatus.type === 'enrolled' && options.fireExperimentExposure;
	});

	const fireExposure = useCallback(() => {
		if (exposureFired) {
			console.warn(`Exposure event for ${experimentName} already fired.`);
		} else if (experimentStatus.type === 'enrolled') {
			experimentStatus.fireExposure();
			setExposureFired(true);
		} else {
			console.warn(
				`Exposure event not fired for ${experimentName}; status is not enrolled or indeterminate.`,
			);
		}
	}, [exposureFired, experimentName, experimentStatus]);

	return [exposureFired, fireExposure];
}
